import PhotoIcon from "@heroicons/react/24/outline/PhotoIcon";
import DocumentTextIcon from "@heroicons/react/24/outline/DocumentTextIcon";
import ClipboardDocumentCheckIcon from "@heroicons/react/24/outline/ClipboardDocumentCheckIcon";
import BuildingStorefrontIcon from "@heroicons/react/24/outline/BuildingStorefrontIcon";
import CurrencyDollarIcon from "@heroicons/react/24/outline/CurrencyDollarIcon";
import DocumentIcon from "@heroicons/react/24/outline/DocumentIcon";
import currencyFormatter from "../../currency";

interface ProjectStatsProps {
  totalJobs: number;
  totalTasks: number;
  completedTasks: number;
  pendingTasks: number;
  totalInvoices: number;
  totalEstimates: number;
  totalPhotos: number;
  totalAttachments: number;
  totalValue: number;
  paidAmount: number;
  dueAmount: number;
  onViewPhotos?: () => void;
}

export default function ProjectStats({
  totalJobs,
  totalTasks,
  completedTasks,
  pendingTasks,
  totalInvoices,
  totalEstimates,
  totalPhotos,
  totalAttachments,
  totalValue,
  paidAmount,
  dueAmount,
  onViewPhotos,
}: ProjectStatsProps) {
  const completionPercentage =
    totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
  const paymentPercentage =
    totalValue > 0 ? Math.round((paidAmount / totalValue) * 100) : 0;

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {/* Jobs Card */}
      <div className="rounded-lg border border-gray-200 bg-white p-4 shadow">
        <div className="flex items-center">
          <div className="rounded-full bg-blue-100 p-3">
            <BuildingStorefrontIcon className="h-6 w-6 text-blue-600" />
          </div>
          <div className="ml-4">
            <h2 className="text-lg font-semibold text-gray-900">{totalJobs}</h2>
            <p className="text-sm text-gray-500">Jobs</p>
          </div>
        </div>
      </div>

      {/* Tasks Card */}
      <div className="rounded-lg border border-gray-200 bg-white p-4 shadow">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="rounded-full bg-green-100 p-3">
              <ClipboardDocumentCheckIcon className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <h2 className="text-lg font-semibold text-gray-900">
                {completedTasks}/{totalTasks}
              </h2>
              <p className="text-sm text-gray-500">Tasks Complete</p>
            </div>
          </div>
          <div className="flex flex-col items-end">
            <div className="text-sm font-medium text-gray-500">
              {completionPercentage}%
            </div>
            <div className="mt-1 h-2.5 w-24 rounded-full bg-gray-200">
              <div
                className="h-2.5 rounded-full bg-green-600"
                style={{ width: `${completionPercentage}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {/* Invoices Card */}
      <div className="rounded-lg border border-gray-200 bg-white p-4 shadow">
        <div className="flex items-center">
          <div className="rounded-full bg-purple-100 p-3">
            <DocumentIcon className="h-6 w-6 text-purple-600" />
          </div>
          <div className="ml-4">
            <h2 className="text-lg font-semibold text-gray-900">
              {totalInvoices}
            </h2>
            <p className="text-sm text-gray-500">Invoices</p>
          </div>
        </div>
      </div>

      {/* Estimates Card */}
      <div className="rounded-lg border border-gray-200 bg-white p-4 shadow">
        <div className="flex items-center">
          <div className="rounded-full bg-yellow-100 p-3">
            <DocumentTextIcon className="h-6 w-6 text-yellow-600" />
          </div>
          <div className="ml-4">
            <h2 className="text-lg font-semibold text-gray-900">
              {totalEstimates}
            </h2>
            <p className="text-sm text-gray-500">Estimates</p>
          </div>
        </div>
      </div>

      {/* Photos Card */}
      <div className="rounded-lg border border-gray-200 bg-white p-4 shadow">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="rounded-full bg-pink-100 p-3">
              <PhotoIcon className="h-6 w-6 text-pink-600" />
            </div>
            <div className="ml-4">
              <h2 className="text-lg font-semibold text-gray-900">
                {totalPhotos}
              </h2>
              <p className="text-sm text-gray-500">Photos</p>
            </div>
          </div>
          {onViewPhotos && totalPhotos > 0 && (
            <button
              onClick={onViewPhotos}
              className="rounded-md bg-pink-600 px-3 py-1 text-xs font-medium text-white hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2"
            >
              View Photos
            </button>
          )}
        </div>
      </div>

      {/* Attachments Card */}
      <div className="rounded-lg border border-gray-200 bg-white p-4 shadow">
        <div className="flex items-center">
          <div className="rounded-full bg-indigo-100 p-3">
            <DocumentTextIcon className="h-6 w-6 text-indigo-600" />
          </div>
          <div className="ml-4">
            <h2 className="text-lg font-semibold text-gray-900">
              {totalAttachments}
            </h2>
            <p className="text-sm text-gray-500">Attachments</p>
          </div>
        </div>
      </div>

      {/* Financial Summary Card */}
      <div className="col-span-1 rounded-lg border border-gray-200 bg-white p-4 shadow md:col-span-2">
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-center">
            <div className="rounded-full bg-green-100 p-3">
              <CurrencyDollarIcon className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <h2 className="text-lg font-semibold text-gray-900">
                {currencyFormatter(totalValue, "USD")}
              </h2>
              <p className="text-sm text-gray-500">Total Value</p>
            </div>
          </div>
          <div className="flex flex-col items-end">
            <div className="text-sm font-medium text-gray-500">
              {paymentPercentage}% Paid
            </div>
            <div className="mt-1 h-2.5 w-24 rounded-full bg-gray-200">
              <div
                className="h-2.5 rounded-full bg-green-600"
                style={{ width: `${paymentPercentage}%` }}
              ></div>
            </div>
          </div>
        </div>

        <div className="mt-4 flex justify-between text-sm">
          <div>
            <span className="text-gray-500">Paid: </span>
            <span className="font-medium text-green-600">
              {currencyFormatter(paidAmount, "USD")}
            </span>
          </div>
          <div>
            <span className="text-gray-500">Due: </span>
            <span className="font-medium text-red-600">
              {currencyFormatter(dueAmount, "USD")}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
